// Number and time formatting. One place, so the board, the public view and the
// text an agent reads all say the same thing about the same value.

import type { NumberFormat } from "./types";

export function formatNumber(v: number | null | undefined, f: NumberFormat | null | undefined = null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const fmt = f ?? {};
  const decimals = fmt.decimals;
  let s: string;
  switch (fmt.style) {
    case "currency": {
      const currency = (fmt.currency || "USD").toUpperCase();
      const big = Math.abs(v) >= 100000;
      try {
        s = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          notation: big ? "compact" : "standard",
          maximumFractionDigits: decimals ?? (big ? 1 : Math.abs(v) < 100 && v % 1 !== 0 ? 2 : 0),
          minimumFractionDigits: decimals ?? 0,
        }).format(v);
      } catch {
        s = "$" + v.toFixed(decimals ?? 0);
      }
      break;
    }
    case "percent": {
      // short of 100 never reads as 100: just under it, two decimals, cut rather than rounded up (99.98%, 99.99%)
      const near = decimals === undefined && v > 99 && v < 100;
      s = new Intl.NumberFormat("en-US", { maximumFractionDigits: decimals ?? (near ? 2 : 1) }).format(near ? Math.floor(v * 100) / 100 : v) + "%";
      break;
    }
    case "compact":
      s = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: decimals ?? 1 }).format(v);
      break;
    case "duration":
      s = formatDuration(v);
      break;
    default: {
      const big = Math.abs(v) >= 1_000_000;
      s = new Intl.NumberFormat("en-US", {
        notation: big ? "compact" : "standard",
        // two decimals below a thousand, so 99.98 is never written as 100
        maximumFractionDigits: decimals ?? (big ? 1 : Math.abs(v) < 1000 && v % 1 !== 0 ? 2 : v % 1 !== 0 ? 1 : 0),
        minimumFractionDigits: decimals ?? 0,
      }).format(v);
    }
  }
  return (fmt.prefix ?? "") + s + (fmt.suffix ?? "");
}

export function formatDuration(seconds: number): string {
  const s = Math.round(Math.abs(seconds));
  const sign = seconds < 0 ? "-" : "";
  if (s < 60) return `${sign}${s}s`;
  if (s < 3600) return `${sign}${Math.floor(s / 60)}m ${s % 60 ? (s % 60) + "s" : ""}`.trim();
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${sign}${h}h${m ? " " + m + "m" : ""}`;
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function timeAgo(t: number | null | undefined, now = Date.now()): string {
  if (!t) return "never";
  const d = Math.max(0, now - t);
  if (d < 45_000) return "just now";
  if (d < HOUR) return `${Math.round(d / MIN)}m ago`;
  if (d < DAY) return `${Math.round(d / HOUR)}h ago`;
  if (d < 30 * DAY) return `${Math.round(d / DAY)}d ago`;
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Since when, in the reader's own days: "since 10:18" today, "since yesterday", "since Tue" this week, else "since 20 Oct". */
export function sinceWords(t: number, now = Date.now()): string {
  const d = new Date(t);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(new Date(now)) - day(d)) / DAY);
  if (days <= 0) return `since ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  if (days === 1) return "since yesterday";
  if (days < 7) return `since ${d.toLocaleDateString([], { weekday: "short" })}`;
  return `since ${d.toLocaleDateString([], { day: "numeric", month: "short" })}`;
}

export function shortAgo(t: number | null | undefined, now = Date.now()): string {
  if (!t) return "";
  const d = Math.max(0, now - t);
  if (d < 45_000) return "now";
  if (d < HOUR) return `${Math.round(d / MIN)}m`;
  if (d < DAY) return `${Math.round(d / HOUR)}h`;
  return `${Math.round(d / DAY)}d`;
}
